import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionControls = ({
  isVideoEnabled,
  isAudioEnabled,
  isScreenSharing,
  isRecording,
  canRecord,
  canShareScreen,
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onToggleRecording,
  onEndSession,
  onOpenChat,
  onOpenSettings
}) => {
  const [showRecordingConsent, setShowRecordingConsent] = useState(false);

  const handleRecordingToggle = () => {
    if (!isRecording && canRecord) {
      setShowRecordingConsent(true);
    } else {
      onToggleRecording();
    }
  };

  const confirmRecording = () => {
    setShowRecordingConsent(false);
    onToggleRecording();
  };

  return (
    <>
      <div className="flex items-center justify-center gap-3 px-6 py-4 bg-card border-t border-border">
        {/* Audio Control */}
        <Button
          variant={isAudioEnabled ? "default" : "destructive"}
          size="lg"
          iconName={isAudioEnabled ? "Mic" : "MicOff"}
          onClick={onToggleAudio}
          className="w-14 h-14 rounded-full"
          title={isAudioEnabled ? "Mute microphone" : "Unmute microphone"}
        />

        {/* Video Control */}
        <Button
          variant={isVideoEnabled ? "default" : "destructive"}
          size="lg"
          iconName={isVideoEnabled ? "Video" : "VideoOff"}
          onClick={onToggleVideo}
          className="w-14 h-14 rounded-full"
          title={isVideoEnabled ? "Turn off camera" : "Turn on camera"}
        />

        {/* Screen Share (Licensed Professionals Only) */}
        {canShareScreen && (
          <Button
            variant={isScreenSharing ? "secondary" : "outline"}
            size="lg"
            iconName={isScreenSharing ? "MonitorOff" : "Monitor"}
            onClick={onToggleScreenShare}
            className="w-14 h-14 rounded-full"
            title={isScreenSharing ? "Stop sharing" : "Share screen"}
          />
        )}

        {/* Recording Control */}
        {canRecord && (
          <Button
            variant={isRecording ? "destructive" : "outline"}
            size="lg"
            iconName={isRecording ? "StopCircle" : "Circle"}
            onClick={handleRecordingToggle}
            className="w-14 h-14 rounded-full"
            title={isRecording ? "Stop recording" : "Start recording"}
          />
        )}

        {/* Chat */}
        <Button
          variant="outline"
          size="lg"
          iconName="MessageSquare"
          onClick={onOpenChat}
          className="w-14 h-14 rounded-full"
          title="Open chat"
        />

        {/* Settings */}
        <Button
          variant="outline"
          size="lg"
          iconName="Settings"
          onClick={onOpenSettings}
          className="w-14 h-14 rounded-full"
          title="Session settings"
        />

        {/* End Session */}
        <Button
          variant="destructive"
          size="lg"
          iconName="PhoneOff"
          onClick={onEndSession}
          className="w-14 h-14 rounded-full ml-4"
          title="End session"
        />
      </div>

      {/* Recording Consent Modal */}
      {showRecordingConsent && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Recording Consent Required
                </h3>
                <p className="text-sm text-muted-foreground">
                  This session will be recorded for quality assurance and compliance purposes. Both participants must provide explicit consent before recording begins.
                </p>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-4 mb-4">
              <ul className="text-sm text-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Recording will be encrypted and stored securely</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Access limited to authorized personnel only</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Retained per organizational policy (typically 90 days)</span>
                </li>
              </ul>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowRecordingConsent(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                fullWidth
                onClick={confirmRecording}
              >
                I Consent to Recording
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionControls;